import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

interface VictimReport {
  id: number;
  report_uid: string;
  case_number: string | null;
  child_name: string;
  incident_type: string;
  urgency: string;
  location: string;
  address: string;
  barangay: string;
  municipality: string;
  province: string;
  status: string;
  case_status: string | null;
  assigned_social_worker_uid: string | null;
  social_worker_name: string | null;
  created_at: string;
}


interface WalkInCase {
  case_intake_id: number;
  case_number: string;
  uid: string | null;
  child_full_name: string;
  incident_type: string;
  urgency: string;
  location: string;
  municipality: string | null;
  province: string | null;
  case_status: string | null;
  assigned_social_worker_uid: string | null;
  assigned_social_worker_name: string | null;
  created_at: string;
}


const API_BASE = 'https://guardiancloudcare.com/backend/api';

export default function SupVictimReportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [reports, setReports] = useState<VictimReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  
  const [walkInCases, setWalkInCases] = useState<WalkInCase[]>([]);
  const [walkInLoading, setWalkInLoading] = useState(true);
  const [walkInError, setWalkInError] = useState('');


  const loadReports = async () => {
    if (!user?.uid) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${API_BASE}/get_supervisor_victim_reports.php?supervisor_uid=${encodeURIComponent(
          user.uid
        )}`
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(
          data.message || 'Unable to retrieve victim reports.'
        );
      }

      setReports(data.reports || []);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to load victim reports.');
    } finally {
      setLoading(false);
    }
  };

  
  const loadWalkInCases = async () => {
    if (!user?.uid) return;

    try {
      setWalkInLoading(true);
      setWalkInError('');

      const response = await fetch(
        `${API_BASE}/get_supervisor_walkin_cases.php?supervisor_uid=${encodeURIComponent(
          user.uid
        )}`
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || 'Unable to retrieve walk-in cases.'
        );
      }

      setWalkInCases(
        Array.isArray(data.cases) ? data.cases : []
      );
    } catch (err: any) {
      console.error('Walk-in cases error:', err);
      setWalkInError(
        err.message || 'Failed to load walk-in cases.'
      );
    } finally {
      setWalkInLoading(false);
    }
  };


  
  useEffect(() => {
    if (!user?.uid) return;

    void loadReports();
    void loadWalkInCases();
  }, [user?.uid]);


  const filteredReports = useMemo(() => {
    const keyword = search.toLowerCase().trim();

    return reports.filter((report) => {

      const matchesSearch =
        keyword === '' ||
        report.report_uid?.toLowerCase().includes(keyword) ||
        report.case_number?.toLowerCase().includes(keyword) ||
        report.child_name?.toLowerCase().includes(keyword) ||
        report.incident_type?.toLowerCase().includes(keyword) ||
        report.municipality?.toLowerCase().includes(keyword) ||
        report.province?.toLowerCase().includes(keyword);

      const matchesFilter =
        filter === 'all' ||
        (filter === 'pending' &&
          !report.assigned_social_worker_uid) ||
        (filter === 'assigned' &&
          !!report.assigned_social_worker_uid);

      return matchesSearch && matchesFilter;
    });
  }, [reports, search, filter]);

  const getUrgencyClass = (urgency: string) => {
    const value = urgency?.toLowerCase();

    if (value === 'immediate danger') {
      return 'bg-red-100 text-red-700';
    }

    if (value === 'urgent') {
      return 'bg-orange-100 text-orange-700';
    }

    return 'bg-blue-100 text-blue-700';
  };

  return (
    <AppLayout
      title="Victim Reports"
      breadcrumb={['Supervisor', 'Victim Reports']}
    >
      <div className="space-y-6">

        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Victim Reports
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Review submitted victim reports and manage case assignments.
          </p>
        </div>

        
        {/* SUMMARY */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-sm text-gray-500">
              Total Cases
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {reports.length + walkInCases.length}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-sm text-gray-500">
              Needs Assignment
            </p>
            <p className="text-3xl font-bold text-orange-600 mt-2">
              {reports.filter(
                (r) => !r.assigned_social_worker_uid
              ).length +
                walkInCases.filter(
                  (c) => !c.assigned_social_worker_uid
                ).length}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-sm text-gray-500">
              Assigned
            </p>
            <p className="text-3xl font-bold text-teal-600 mt-2">
              {reports.filter(
                (r) => !!r.assigned_social_worker_uid
              ).length +
                walkInCases.filter(
                  (c) => !!c.assigned_social_worker_uid
                ).length}
            </p>
          </div>
        </div>


        {/* FILTERS */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">

          <div className="flex flex-col md:flex-row gap-3">

            <div className="flex-1 relative">

              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search report, child, case or location..."
                className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:border-teal-500"
              />

            </div>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-500"
            >
              <option value="all">All Reports</option>
              <option value="pending">Needs Assignment</option>
              <option value="assigned">Assigned</option>
            </select>

            <button
              onClick={() => {
                void loadReports();
                void loadWalkInCases();
              }}
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm hover:bg-gray-50"
            >
              <i className="ri-refresh-line mr-1" />
              Refresh
            </button> 

          </div>

        </div>

        {/* ERROR */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
            <p className="font-medium">
              Unable to load reports
            </p>

            <p className="text-sm mt-1">
              {error}
            </p>
          </div>
        )}

        {/* TABLE */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-800">
              Submitted Victim Reports
            </h2>
          </div>

          {loading ? (

            <div className="p-10 text-center text-gray-500">
              <i className="ri-loader-4-line animate-spin text-xl" />

              <p className="text-sm mt-2">
                Loading victim reports...
              </p>
            </div>

          ) : filteredReports.length === 0 ? (

            <div className="p-10 text-center">

              <i className="ri-file-search-line text-4xl text-gray-300" />

              <p className="font-medium text-gray-700 mt-3">
                No victim reports found
              </p>

              <p className="text-sm text-gray-400 mt-1">
                There are no reports matching your current filter.
              </p>

            </div>

          ) : (

            <div className="overflow-x-auto">

              <table className="w-full">

                <thead className="bg-gray-50">

                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Report
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Child
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Incident
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Location
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Urgency
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Assignment
                    </th>

                    <th className="text-right text-xs font-semibold text-gray-500 px-5 py-3">
                      Action
                    </th>
                  </tr>

                </thead>

                <tbody className="divide-y divide-gray-100">

                  {filteredReports.map((report) => (

                    <tr
                      key={report.report_uid}
                      className="hover:bg-gray-50"
                    >

                      <td className="px-5 py-4">

                        <p className="font-semibold text-gray-800 text-sm">
                          {report.report_uid}
                        </p>

                        {report.case_number && (
                          <p className="text-xs text-gray-400 mt-1">
                            {report.case_number}
                          </p>
                        )}

                      </td>

                      <td className="px-5 py-4">

                        <p className="text-sm font-medium text-gray-800">
                          {report.child_name || 'Not provided'}
                        </p>

                      </td>

                      <td className="px-5 py-4">

                        <p className="text-sm text-gray-700">
                          {report.incident_type}
                        </p>

                      </td>

                      <td className="px-5 py-4">

                        <p className="text-sm text-gray-700">
                          {report.municipality ||
                            report.location ||
                            'Not provided'}
                        </p>

                        <p className="text-xs text-gray-400">
                          {report.province}
                        </p>

                      </td>

                      <td className="px-5 py-4">

                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getUrgencyClass(
                            report.urgency
                          )}`}
                        >
                          {report.urgency || 'Not specified'}
                        </span>

                      </td>

                      <td className="px-5 py-4">

                        {report.social_worker_name ? (

                          <div>
                            <p className="text-sm font-medium text-teal-700">
                              {report.social_worker_name}
                            </p>

                            <p className="text-xs text-gray-400">
                              Assigned
                            </p>
                          </div>

                        ) : (

                          <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Needs Assignment
                          </span>

                        )}

                      </td>

                      <td className="px-5 py-4 text-right">

                        <button
                        onClick={() =>
                            navigate(
                            `/sup-victim-reports/${encodeURIComponent(
                                report.report_uid
                            )}`
                            )
                        }
                        className="text-sm text-teal-600 hover:text-teal-800 font-medium"
                        >
                        Review
                        </button>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          )}

        </div>

        
        {/* WALK-IN CASES TABLE */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-800">
              Walk-in Cases
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Cases entered directly through case intake rather than submitted
              through the victim reporting process.
            </p>
          </div>

          {walkInError && (
            <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {walkInError}
            </div>
          )}

          {walkInLoading ? (
            <div className="p-8 text-center text-gray-500">
              Loading walk-in cases...
            </div>
          ) : walkInCases.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No walk-in cases found for your office.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">

                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Report
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Child
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Incident
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Location
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Urgency
                    </th>

                    <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">
                      Assignment
                    </th>

                    <th className="text-right text-xs font-semibold text-gray-500 px-5 py-3">
                      Action
                    </th>
                  </tr>

                </thead>

                <tbody className="divide-y divide-gray-100">
                  {walkInCases.map((item) => (
                    <tr key={item.case_number} className="hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <p className="text-sm font-semibold text-gray-800">
                          {item.case_number}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Walk-in
                        </p>
                      </td>

                      <td className="px-5 py-4 text-sm text-gray-800">
                        {item.child_full_name || 'Not provided'}
                      </td>

                      <td className="px-5 py-4 text-sm text-gray-700">
                        {item.incident_type || 'Not specified'}
                      </td>

                      <td className="px-5 py-4 text-sm text-gray-700">
                        {item.location || item.municipality || 'Not provided'}
                        {item.province ? `, ${item.province}` : ''}
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-700">
                          {item.urgency || 'Not specified'}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-sm text-teal-700">
                        {item.assigned_social_worker_name || 'Unassigned'}
                      </td>

                      
                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/sup-walkin-case-details/${encodeURIComponent(item.case_number)}`
                            )
                          }
                          className="text-sm font-medium text-teal-600 hover:text-teal-800"
                        >
                          Review
                        </button>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>


      </div>
    </AppLayout>
  );
}